import React, { useEffect, useRef, forwardRef } from 'react';
import {
    Popper,
    Paper,
    List,
    ListItem,
    Typography,
    Link,
    CircularProgress,
    Tooltip,
} from '@mui/material';

import iconFolder from "../../../../assets/yellow.svg";
import filePdf from "../../../../assets/file-pdf.png";
import fileWord from "../../../../assets/file-word.png";
import fileExcel from "../../../../assets/file-excel.png";
import fileImage from "../../../../assets/file-jpg.png";
import fileZip from "../../../../assets/file-zip.png";
import fileDoc from "../../../../assets/file-doc.png";
import fileOther from "../../../../assets/file-other.png";

const getFileIcon = (file) => {
    if (file.folder || file.fkArchive?.type === 'Folder') {
        return iconFolder;
    }

    switch (file.fkArchive?.originalExtension?.toLowerCase()) {
        case '.pdf': return filePdf;
        case '.doc':
        case '.docx': return fileWord;
        case '.xls':
        case '.xlsx':
        case '.csv': return fileExcel;
        case '.jpg':
        case '.jpeg':
        case '.png': return fileImage;
        case '.zip': return fileZip;
        case '.txt': return fileDoc;
        default: return fileOther;
    }
};

const SearchAutocomplete = forwardRef(({
    anchorEl,
    results,
    onSelect,
    onViewAll,
    onClose,
    currentPath,
    loading,
    query,
}, ref) => {
    // Don't show dropdown if query is empty, even if loading
    const shouldShowDropdown = Boolean(anchorEl) && query && query.trim() !== "" && ((results && results.length > 0) || loading);
    
    return (
        <Popper
            open={shouldShowDropdown}
            anchorEl={anchorEl}
            placement="bottom-start"
            style={{ 
                width: anchorEl ? anchorEl.offsetWidth : 290, 
                zIndex: 1300,
                marginTop: '0px'
            }}
            modifiers={[
                {
                    name: 'offset',
                    options: {
                        offset: [0, 0],
                    },
                },
            ]}
        >
            <Paper
                ref={ref}
                elevation={3}
                style={{
                    maxHeight: 400,
                    overflow: 'auto',
                    width: '100%',
                    boxSizing: 'border-box',
                }}
            >
                <List dense>
                    {loading && query && query.trim() !== "" && (
                        <ListItem
                            sx={{
                                display: 'flex',
                                gap: 1,
                                alignItems: 'center',
                                py: 1,
                                px: 1.5,
                                borderBottom: results && results.length > 0 ? '1px solid #eee' : 'none',
                            }}
                        >
                            <CircularProgress size={18} />
                            <Typography variant="body2" color="textSecondary">
                                Searching…
                            </Typography>
                        </ListItem>
                    )}

                    {results.map((file, index) => (
                        <div
                            key={index}
                            onClick={() => onSelect(file)}
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                padding: '8px 12px',
                                borderBottom: '1px solid #eee',
                                cursor: 'pointer',
                                gap: '10px',
                                width: '100%',
                            }}
                        >
                            <div style={{ flexShrink: 0 }}>
                                <img
                                    src={getFileIcon(file)}
                                    alt="file-icon"
                                    style={{ width: 30, height: 30 }}
                                />
                            </div>
                            <div style={{ flex: 1, minWidth: 0, overflow: 'hidden' }}>
                                <Tooltip title={file.fkArchive?.name || ""} placement="top" arrow>
                                    <Typography
                                        variant="body2"
                                        sx={{ fontWeight: 500, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
                                    >
                                        {file.fkArchive?.name}
                                    </Typography>
                                </Tooltip>
                                <Tooltip title={file.fkArchive?.path || ""} placement="top" arrow>
                                    <Typography
                                        variant="caption"
                                        color="textSecondary"
                                        sx={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', display: 'block' }}
                                    >
                                        {file.fkArchive?.path || "/.../" + file.fkArchive?.name}
                                    </Typography>
                                </Tooltip>
                            </div>
                            <div style={{ flexShrink: 0, minWidth: 80 }}>
                                <Typography
                                    variant="caption"
                                    color="textSecondary"
                                    sx={{
                                        fontStyle: 'italic',
                                        textAlign: 'right',
                                        whiteSpace: 'nowrap',
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis'
                                    }}
                                >
                                    {file.fkArchive?.type || "No Doctype"}
                                </Typography>
                            </div>
                        </div>
                    ))}

                    {!loading && (!results || results.length === 0) && query && (
                        <ListItem sx={{ py: 1, px: 1.5 }}>
                            <Typography variant="body2" color="textSecondary">
                                No results
                            </Typography>
                        </ListItem>
                    )}

                    <ListItem
                        button
                        onClick={() => {
                            onViewAll();
                            onClose();
                        }}
                        sx={{
                            justifyContent: 'center',
                            borderTop: '1px solid #ddd',
                            py: 1,
                        }}
                    >
                        <Link component="button" variant="body2">
                            View All
                        </Link>
                    </ListItem>
                </List>
            </Paper>
        </Popper>
    );
});

export default SearchAutocomplete;
